import type { ReactNode, Ref } from 'react';

import type { CheckboxProps } from './Checkbox';

/**
 * Maps the legacy MUI `Checkbox` props onto the Design System 2.0 API.
 *
 * TODO(HNS-264): temporary, for the same reason as `ReactHookForm/Switch/toDsProps` — the visual
 * refresh is gated inside the wrapper instead of at every call site, and retiring the flag means
 * deleting this and letting call sites speak the DS vocabulary directly.
 *
 * Returning `null` means "the DS checkbox cannot express this one" and sends the caller back to MUI.
 * A silent best-effort mapping would ship controls that look or behave subtly wrong, with nothing to
 * catch it at review time.
 *
 * `checked` and `onChange` are the wrapper's business (the DS change handler has a different shape),
 * so they are not part of the input.
 */
type MappableProps = Omit<CheckboxProps, 'checked' | 'onChange'>;

/**
 * Mirrors the union in `packages/design-system/src/Checkbox/types.ts` rather than flattening it, so
 * the mapper's guarantee ("this has a name") is visible in its type and the wrapper can spread it
 * without a cast. Same reasoning as `ReactHookForm/Switch/toDsProps`.
 */
type NameProps =
  | { label: ReactNode }
  | { label?: undefined; 'aria-label': string }
  | { label?: undefined; 'aria-labelledby': string };

export type DsProps = NameProps & {
  invalid?: boolean;
  errorMessage?: string;
  name?: string;
  value?: string;
  disabled?: boolean;
  required?: boolean;
  indeterminate?: boolean;
  inputRef?: Ref<HTMLInputElement>;
  /** Handlers, `aria-*`, `data-*`, `id` and `title`: plain attributes the DS root forwards to its `<span>`. */
  attributes: Record<string, unknown>;
};

/** Props the mapper reads by name. Anything else has to be a plain DOM attribute or it is refused. */
const READ_BY_NAME = new Set([
  'label',
  'invalid',
  'errorMessage',
  'variant',
  'labelPlacement',
  'labelAlign',
  'name',
  'value',
  'disabled',
  'required',
  'indeterminate',
  'inputRef',
  'aria-label',
  'aria-labelledby',
]);

/**
 * MUI's `Tooltip` clones its child and injects `onMouseOver`, `onFocus`, `onTouchStart` and friends,
 * so handlers have to survive or a tooltip on a checkbox silently never opens.
 */
const isPlainAttribute = (key: string) => /^(on[A-Z]|aria-|data-)/.test(key) || key === 'id' || key === 'title';

/**
 * Cases the DS checkbox cannot express, all of which stay on MUI:
 *
 *   - `sx` / `labelSx` — Emotion props with no effect on a Tailwind control. Accepting them would
 *     silently drop the styling rather than fail.
 *   - `variant="outlined"` — the boxed row has no DS counterpart.
 *   - `labelPlacement` other than `end`, and `labelAlign="top"` — the design draws only the label
 *     after the box, vertically centred.
 *   - `size`, `color`, `icon`, `checkedIcon`, `indeterminateIcon`, `inputProps` and any other prop
 *     not named above, because each one is MUI vocabulary the DS box ignores.
 *   - no accessible name. The DS types require exactly one of `label` / `aria-label` /
 *     `aria-labelledby`; refusing here keeps the wrapper from casting around that check.
 */
export const toDsProps = (props: MappableProps): DsProps | null => {
  const { label, variant, labelPlacement, labelAlign, name, value, disabled, required, indeterminate, inputRef } =
    props;
  const ariaLabel = props['aria-label'];
  const ariaLabelledBy = props['aria-labelledby'];

  if (variant === 'outlined' || labelAlign === 'top') {
    return null;
  }

  if (labelPlacement !== undefined && labelPlacement !== 'end') {
    return null;
  }

  const attributes: Record<string, unknown> = {};

  for (const [key, propValue] of Object.entries(props)) {
    // MUI call sites pass `undefined` and empty strings routinely (`className=""` from Tooltip).
    if (propValue === undefined || propValue === null || propValue === '' || READ_BY_NAME.has(key)) {
      continue;
    }

    if (!isPlainAttribute(key)) {
      return null;
    }

    attributes[key] = propValue;
  }

  const shared = {
    invalid: props.invalid,
    errorMessage: props.errorMessage,
    name,
    // RHF hands the field's boolean value through; Base UI's `value` is the submitted string.
    value: typeof value === 'string' ? value : undefined,
    disabled,
    required,
    indeterminate,
    inputRef,
    attributes,
  };

  // Early returns rather than one built object, so each satisfies a single arm of the union above.
  if (label) {
    return { ...shared, label };
  }

  if (ariaLabelledBy) {
    return { ...shared, 'aria-labelledby': ariaLabelledBy };
  }

  if (ariaLabel) {
    return { ...shared, 'aria-label': ariaLabel };
  }

  return null;
};
